import { HttpClient } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export type ChatThinkingLevel = 'low' | 'medium' | 'high';
export type ChatModelGroup = 'gemini' | 'claude';

/**
 * GET /ai/models 응답 항목.
 * 카탈로그 첫 번째 항목이 default — 백엔드가 의도된 우선순위 순서로 내려줌.
 */
export interface ChatModelOption {
  id: string;
  label: string;
  description?: string;
  group: ChatModelGroup;
  thinkingLevel?: ChatThinkingLevel;
}

/**
 * 요청 하나의 상한. Lambda 동기 호출 페이로드 한도(6MiB)는 Function URL 과 API Gateway 경로에
 * 똑같이 걸리고, 본문이 아니라 헤더까지 포함한 이벤트 전체 기준이라 여유를 둔다.
 */
export const MAX_CHAT_REQUEST_BYTES = 5.5 * 1024 * 1024;

/** 업스트림 원문(AWS JSON 등)을 그대로 보여주지 않기 위한 상태코드별 안내. */
function describeHttpFailure(status: number): string {
  if (status === 401 || status === 403) {
    return 'AI 어드바이저 사용 권한이 없습니다. 관리자에게 문의해주세요.';
  }
  if (status === 413) {
    return '요청이 너무 큽니다. 첨부를 줄이거나 새 대화로 시작해주세요.';
  }
  if (status === 429) {
    return '지금 다른 요청이 처리 중입니다. 잠시 후 다시 시도해주세요.';
  }
  return 'AI 응답을 받지 못했습니다. 잠시 후 다시 시도해주세요.';
}

export interface AiAccessResponse {
  aiProductAdvisor: boolean;
  /** chat-stream 전용 Function URL. 미제공이면 API Gateway 경로로 폴백한다. */
  streamUrl?: string;
}

export interface AiStreamChunk {
  type: 'start' | 'content' | 'complete' | 'error';
  delta?: string;
  accumulated?: string;
  finalText?: string;
  reasoning?: string;
  tokensUsed?: number;
  message?: string;
  error?: string;
  metadata?: any;
  productData?: Partial<ProductContext>;
  /** start 청크에서 백엔드가 실제 사용한 모델을 echo. 카탈로그 fallback 동기화용. */
  modelId?: string;
  modelLabel?: string;
}

export interface ProductPolicy {
  refundPolicy: string;
  notice?: string;
  checkList?: string;
  includeList?: string;
  excludeList?: string;
}

export interface ProductLocation {
  address: string;
  detailAddress?: string;
  sido: string;
  sigugun: string;
  country: string;
  lat?: number;
  lon?: number;
  howToCome?: string;
}

export interface ProductDuration {
  days: number;
  hours: number;
  minutes: number;
}

export interface Hashtag {
  id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
}

export interface Summary {
  title: string;
  description: string;
  photos: string[];
}

export interface Paragraph {
  title: string;
  description: string;
  photos: string[];
  time?: string;
  note?: string;
}

export interface Section {
  title: string;
  paragraphs: Paragraph[];
}

export interface RepresentationPhoto {
  id?: number;
  photo: string;
}

export interface ProductContext {
  productId: number;
  title: string;
  price: number;
  discountPrice?: number;
  description?: string;
  recommend?: string;
  point?: string;
  location: ProductLocation;
  duration: ProductDuration;
  policies: ProductPolicy;
  summaries?: Summary[];
  sections?: Section[];
  categories?: Category[];
  hashtags?: Hashtag[];
  representationPhotos?: RepresentationPhoto[];
}

export interface ConversationTurn {
  isUser: boolean;
  content: string;
}

export interface ChatAttachmentDto {
  name: string;
  type: 'image' | 'pdf';
  size: number;
  mediaType?: string;
  base64?: string;
}

export interface UploadedAttachmentDto {
  name: string;
  type: string;
  mediaType: string;
  size: number;
  base64: string;
}

export interface ProductImageAnalysisDto {
  headline?: string;
  description?: string;
  suitable_section?: string;
  tags?: {
    objects: string[];
    location_features: string[];
    mood_and_style: string[];
    potential_hashtags: string[];
  };
}

export interface ProductImageDto {
  id: string;
  url: string;
  name: string;
  file?: File;
  isLoading: boolean;
  analysis?: ProductImageAnalysisDto;
}

export interface ImageEnhancementResult {
  enhancedImageBase64: string;
  mimeType: string;
  metadata?: any;
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  /**
   * chat-stream 전용 Function URL 조회. 화면마다 /ai/access 를 부르는지가 제각각이라
   * (어드민 상품 편집 화면은 부르지 않는다) 서비스가 직접 한 번 조회해 캐시한다.
   */
  private streamUrlLookup?: Promise<string | undefined>;

  private fallbackReported = false;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private authService: AuthService,
  ) {}

  uploadFiles(files: File[]): Observable<UploadedAttachmentDto[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    const url = `${environment.apiDomain}/ai/upload`;
    return this.http.post<UploadedAttachmentDto[]>(url, formData);
  }

  analyzeImage(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('image', file, file.name);
    const url = `${environment.apiDomain}/ai/analyze-image`;
    return this.http.post<any>(url, formData);
  }

  /**
   * Gemini 이미지 모델(백엔드 폴백 체인)을 사용한 이미지 보정
   * - imageUrl을 백엔드로 전송하여 CORS 문제 회피
   */
  async enhanceImage(
    imageUrl: string,
    type: 'quality' | 'regenerate' | 'add_people' | 'clear_weather' | 'custom',
    customPrompt?: string,
  ): Promise<ImageEnhancementResult> {
    // FormData 생성 (imageUrl만 전송)
    const formData = new FormData();
    formData.append('imageUrl', imageUrl);
    formData.append('type', type);
    if (customPrompt) {
      formData.append('prompt', customPrompt);
    }

    const url = `${environment.apiDomain}/ai/enhance-image`;
    return this.http.post<ImageEnhancementResult>(url, formData).toPromise();
  }

  /**
   * 채팅 셀렉터에 노출할 모델 카탈로그를 백엔드에서 조회.
   * 신규 모델 추가 시 백엔드만 변경하면 프론트는 자동 반영.
   */
  getChatModels(): Observable<{ models: ChatModelOption[] }> {
    return this.http.get<{ models: ChatModelOption[] }>(`${environment.apiDomain}/ai/models`);
  }

  /**
   * 현재 로그인 유저의 AI 상품 어드바이저 사용 가능 여부 조회.
   * host 편집 페이지에서 ai-chat 노출 판단에 사용.
   */
  getAiAccess(): Observable<AiAccessResponse> {
    return this.http.get<AiAccessResponse>(`${environment.apiDomain}/ai/access`);
  }

  private resolveStreamUrl(): Promise<string | undefined> {
    if (!this.streamUrlLookup) {
      this.streamUrlLookup = this.getAiAccess()
        .toPromise()
        .then((access) => {
          const url = access?.streamUrl?.trim();
          return url && url.startsWith('https://') ? url : undefined;
        })
        .catch((error) => {
          // 실패를 캐시하면 일시적 오류 한 번에 세션 내내 30초 캡 경로로 고정된다.
          this.streamUrlLookup = undefined;
          this.reportGatewayFallback('/ai/access 조회 실패', error);
          return undefined;
        });
    }
    return this.streamUrlLookup;
  }

  /** 폴백은 세션당 한 번만 보고한다 — 상시 발동하는 상황에서 이슈가 요청 수만큼 쌓이지 않도록. */
  private reportGatewayFallback(what: string, reason: unknown): void {
    console.warn(`AI 스트리밍 ${what} — 30초 캡이 있는 경로로 되돌아갑니다.`, reason);
    if (this.fallbackReported) {
      return;
    }
    this.fallbackReported = true;
    Sentry.captureMessage('AI chat-stream fell back to the 30s-capped API Gateway path', {
      level: 'warning',
      extra: { what, reason: (reason as Error)?.message ?? String(reason) },
    });
  }

  /**
   * 실시간 스트리밍 AI 채팅
   * - productContext가 제공되면 상품 편집 모드로 작동
   */
  chatStream(
    message: string,
    history: ConversationTurn[] = [],
    productContext?: ProductContext,
    attachments?: ChatAttachmentDto[],
    productImages?: ProductImageDto[],
    modelId?: string,
    thinkingLevel?: ChatThinkingLevel,
  ): Observable<AiStreamChunk> {
    const gatewayUrl = `${environment.apiDomain}/ai/chat-stream`;
    const body = {
      message,
      history,
      productContext,
      attachments,
      productImages,
      modelId,
      thinkingLevel,
    };

    const payload = JSON.stringify(body);
    const size = new Blob([payload]).size;
    if (size >= MAX_CHAT_REQUEST_BYTES) {
      const asMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);
      return of({
        type: 'error' as const,
        error: `대화 내용과 첨부를 합친 요청이 너무 큽니다(현재 ${asMb(size)}MB · 한도 ${asMb(MAX_CHAT_REQUEST_BYTES)}MB). 첨부를 줄이거나 새 대화로 시작해주세요.`,
      });
    }

    const subject = new Subject<AiStreamChunk>();
    const controller = new AbortController();
    const signal = controller.signal;

    this.ngZone.runOutsideAngular(() => {
      const connect = async () => {
        try {
          const token = this.authService.getToken();
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          };
          if (token && token.trim().length > 0) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const request = (target: string) => fetch(target, { method: 'POST', headers, body: payload, signal });

          // API Gateway 통합 타임아웃은 AWS 하드 캡 30초라 30초를 넘는 생성이 503 으로 잘린다.
          // Function URL 을 알면 그 캡을 건너뛴다(근거: nnb-nestjs/sst.config.ts NNBAiStream).
          const streamUrl = await this.resolveStreamUrl();
          const target = streamUrl ?? gatewayUrl;
          const viaFunctionUrl = target !== gatewayUrl;

          // 되돌아갔다는 사실을 반드시 남긴다 — 이 PR 이 고친 30초 캡으로 조용히 회귀한 상태다.
          const fallBackToGateway = (reason: unknown) => {
            this.reportGatewayFallback('Function URL 실패', reason);
            return request(gatewayUrl);
          };

          let response: Response;
          try {
            response = await request(target);
          } catch (error) {
            const aborted = signal.aborted || (error as Error)?.name === 'AbortError';
            if (!viaFunctionUrl || aborted) {
              throw error;
            }
            response = await fallBackToGateway(error);
          }

          // 429 는 reserved concurrency 소진이라 요청이 실행되지 않았다 — 재시도해도 생성이 겹치지 않는다.
          if (!response.ok && response.status === 429 && viaFunctionUrl && !signal.aborted) {
            response = await fallBackToGateway('throttled (429)');
          }

          if (!response.ok) {
            console.warn(`AI 스트리밍 실패 HTTP ${response.status}`, await response.text());
            const failure = describeHttpFailure(response.status);
            this.ngZone.run(() => {
              subject.next({ type: 'error', error: failure });
              subject.complete();
            });
            return;
          }

          if (!response.body) {
            throw new Error('No response body');
          }

          const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
          let buffer = '';

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { value, done } = await reader.read();
            if (done) {
              break;
            }

            buffer += value;
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
              const message = buffer.substring(0, boundary);
              buffer = buffer.substring(boundary + 2);

              if (message.startsWith('data: ')) {
                const json = message.substring(6);
                this.ngZone.run(() => {
                  try {
                    const chunk: AiStreamChunk = JSON.parse(json);
                    subject.next(chunk);
                    if (chunk.type === 'complete' || chunk.type === 'error') {
                      controller.abort();
                      subject.complete();
                    }
                  } catch (error) {
                    console.error('Error parsing AI stream chunk:', json, error);
                  }
                });
              }
              boundary = buffer.indexOf('\n\n');
            }
          }
        } catch (error) {
          this.ngZone.run(() => {
            if (error.name !== 'AbortError') {
              console.error('AI Stream connection error:', error);
              subject.error(error);
            }
          });
        }
      };

      connect();
    });

    // 구독 해제 = 호출자가 취소한 것. fetch 와 서버 측 생성을 같이 끊지 않으면 화면만 멈추고
    // Lambda 는 남은 수십 초를 계속 돈다.
    return new Observable<AiStreamChunk>((subscriber) => {
      const inner = subject.subscribe(subscriber);
      return () => {
        inner.unsubscribe();
        controller.abort();
      };
    });
  }
}
